import React from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plan, Employee } from "@shared/schema";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import BenefitsOverview from "../ClientDetailComponents/BenefitsTab/BenefitsOverview";
import AvailablePlans from "../ClientDetailComponents/BenefitsTab/AvailablePlans";
import OpenEnrollment from "../ClientDetailComponents/BenefitsTab/OpenEnrollment";
import EnrollmentsTab from "../ClientDetailComponents/BenefitsTab/EnrollmentsTab";
import EmployeeSummary from "../ClientDetailComponents/BenefitsTab/EmployeeSummary";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { AddPlanDialog } from "@/components/ui/plans/AddPlanDialog";

interface BenefitsTabProps {
  plansData: Plan[];
  employees: Employee[];
  agencyId: string;
  plansLoading: boolean;
  getEffectivePlan: (plan: Plan) => Plan;
  formatDateSafe: (date: string | Date | null | undefined) => string;
  getPlanRate: (planId: number, coverageTier: string) => number;
  calculateContributions: (
    planId: number,
    coverageTier: string,
    totalPremium: number
  ) => { employerContribution: number; employeeContribution: number };
  deletePlanMutation: any;
  setLocation: (location: string) => void;
}

export function BenefitsTab({
  plansData,
  employees,
  agencyId,
  plansLoading,
  getEffectivePlan,
  formatDateSafe,
  getPlanRate,
  calculateContributions,
  deletePlanMutation,
  setLocation,
}: BenefitsTabProps) {
  const [isAddPlanDialogOpen, setIsAddPlanDialogOpen] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState("overview");

  // Filter employees for this agency (similar to clientEmployees in ClientDetail)
  const agencyEmployees = employees.filter(
    (emp) => emp.agencyId === agencyId
  );

  const handleSubTabChange = (newTab: string) => {
    setActiveSubTab(newTab);
  };

  const openEnrollmentsTab = () => {
    setActiveSubTab("enrollments");
  };

  const openAddPlanDialog = () => {
    setIsAddPlanDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Benefits & Plans</h3>
      </div>

      {/* Benefits Sub-navigation with compact button group */}
      <Tabs
        value={activeSubTab}
        onValueChange={handleSubTabChange}
        className="space-y-4"
      >
        <div className="flex items-center gap-2 border-b pb-2">
          <span className="text-sm font-medium text-muted-foreground">
            View:
          </span>
          <TabsList className="h-9 bg-muted">
            <TabsTrigger value="overview" className="text-xs h-7">
              Overview
            </TabsTrigger>
            <TabsTrigger value="enrollments" className="text-xs h-7">
              Enrollments
            </TabsTrigger>
            <TabsTrigger value="open-enrollment" className="text-xs h-7">
              Open Enrollment
            </TabsTrigger>
            <TabsTrigger value="employee-summary" className="text-xs h-7">
              Employee Summary
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview" className="space-y-4">
          <BenefitsOverview
            plansData={plansData}
            employees={employees}
            clientId={agencyId}
            getEffectivePlan={getEffectivePlan}
            formatDateSafe={formatDateSafe}
          />

          <AvailablePlans
            plansData={plansData}
            plansLoading={plansLoading}
            getEffectivePlan={getEffectivePlan}
            formatDateSafe={formatDateSafe}
            deletePlanMutation={deletePlanMutation}
            deleteMode="removeFromAgency"
            setLocation={setLocation}
            agencyId={agencyId}
            onAddPlanClick={openAddPlanDialog}
          />
        </TabsContent>

        <TabsContent value="enrollments" className="space-y-4">
          <EnrollmentsTab
            plansData={plansData}
            clientEmployees={agencyEmployees}
            clientId={agencyId}
          />
        </TabsContent>

        <TabsContent value="open-enrollment" className="space-y-4">
          <OpenEnrollment
            clientEmployees={agencyEmployees}
            clientId={agencyId}
            plansData={plansData}
            plansLoading={plansLoading}
          />
        </TabsContent>


        <TabsContent value="employee-summary" className="space-y-4">
          <EmployeeSummary
            employees={agencyEmployees as any}
            plansData={plansData as any}
            clientId={agencyId}
          />
        </TabsContent>
      </Tabs>

      <ErrorBoundary>
        <AddPlanDialog
          open={isAddPlanDialogOpen}
          onOpenChange={setIsAddPlanDialogOpen}
          agencyId={agencyId}
        />
      </ErrorBoundary>
    </div>
  );
}
